import { hhmm } from '@jobfair/shared';
import { heDate, mmss } from '../lib/format';
import { useTrack } from '../lib/trackerContext';
import type { LiveState, Phase, Site } from '../types';
import { CalLinks } from './CalLinks';
import { Countdown, ExtLink } from './common';
import { Icon } from './Icon';

function Webinar({ st, on }: { st: LiveState; on: boolean }) {
  const track = useTrack();
  if (on && st.webinarUrl) {
    return (
      <ExtLink
        className="btn btn-webinar btn-lg"
        href={st.webinarUrl}
        label="הוובינר המרכזי"
        onOpen={() => track('webinar_click')}
      >
        <Icon n="broadcast" />
        כניסה לוובינר המרכזי
      </ExtLink>
    );
  }
  return (
    <button className="btn btn-lg" disabled>
      <Icon n="lock" />
      {on ? 'קישור הוובינר יפורסם בקרוב' : `הכניסה לוובינר תיפתח ב-${st.convStart}`}
    </button>
  );
}

/** Screen readers get state changes, not a per-second countdown. */
export function statusAnnouncement(ph: Phase): string {
  if (ph.state === 'before') return 'היריד טרם נפתח';
  if (ph.state === 'keynote') return 'ההתכנסות המרכזית מתקיימת עכשיו';
  if (ph.state === 'sessions') {
    return ph.round >= 0 ? `סבב ${ph.round + 1} מתקיים כעת` : 'מפגשי היחידות פתוחים';
  }
  return 'היריד הסתיים';
}

export function StatusPanel({ site, st, ph }: { site: Site; st: LiveState; ph: Phase }) {
  const track = useTrack();
  const now = ph.now;
  const r1 = ph.valid ? hhmm(ph.R[0]!.start) : st.rounds[0].start;
  let body;

  if (ph.state === 'before') {
    body = (
      <>
        <div className="st-eyebrow">
          <Icon n="clock" s={15} />
          לפני היריד · {heDate(st.date, { day: 'numeric', month: 'long' })},{' '}
          <span className="num">{st.convStart}</span>
        </div>
        <h2>היריד ייפתח בעוד</h2>
        {ph.valid && ph.cs > now ? (
          <Countdown ms={ph.cs - now} />
        ) : (
          <p className="st-sub">מועד הפתיחה יעודכן בקרוב.</p>
        )}
        <p className="st-sub">
          בינתיים אפשר לעיין ביחידות, לצפות בסרטונים ולשמור מועדפים. כפתורי המפגשים ייפתחו בתחילת
          הסבב הראשון.
        </p>
        <Webinar st={st} on={false} />
        <CalLinks site={site} st={st} ph={ph} />
      </>
    );
  } else if (ph.state === 'keynote') {
    body = (
      <>
        <div className="st-eyebrow">
          <span className="live-dot" />
          בשידור כעת
        </div>
        <h2>ההתכנסות המרכזית מתקיימת עכשיו</h2>
        <Webinar st={st} on />
        <p className="st-sub">
          המפגשים עם היחידות ייפתחו ב-<b className="num">{r1}</b>
          {st.delay > 0 ? ` (נדחו ב-${st.delay} דקות)` : ''}. אחרי ההתכנסות חזרו לכאן ובחרו יחידה.
        </p>
      </>
    );
  } else if (ph.state === 'sessions') {
    if (ph.round >= 0) {
      const r = ph.R[ph.round]!;
      const pct = Math.min(100, Math.max(0, ((now - r.start) / (r.end - r.start)) * 100));
      body = (
        <>
          <div className="st-eyebrow">
            <span className="live-dot" />
            בשידור כעת · המפגשים פתוחים
          </div>
          <h2>סבב {ph.round + 1} מתקיים כעת</h2>
          <div className="bigtime" role="timer">
            <span>{mmss(r.end - now)}</span>
            <small>נותרו לסיום הסבב</small>
          </div>
          <div className="progress" aria-hidden="true">
            <i style={{ width: pct + '%' }} />
          </div>
          <p className="st-sub">
            בחרו יחידה ולחצו "הצטרפות למפגש". אפשר להצטרף בכל רגע ולעבור בין יחידות, גם אם הגעתם
            באמצע הסבב.
          </p>
        </>
      );
    } else if (ph.next >= 0) {
      const r = ph.R[ph.next]!;
      body = (
        <>
          <div className="st-eyebrow">
            <span className="live-dot" />
            המפגשים פתוחים
          </div>
          <h2>{ph.next === 0 ? 'הסבב הראשון' : 'סבב ' + (ph.next + 1)} יתחיל בעוד</h2>
          <div className="bigtime" role="timer">
            <span>{mmss(r.start - now)}</span>
            <small>דקות</small>
          </div>
          <p className="st-sub">
            אפשר כבר להיכנס למפגשי היחידות. אותו קישור משמש את כל שלושת הסבבים.
          </p>
        </>
      );
    } else {
      body = (
        <>
          <div className="st-eyebrow">
            <span className="live-dot" />
            המפגשים פתוחים
          </div>
          <h2>מפגשי היחידות פתוחים</h2>
          <p className="st-sub">בחרו יחידה ולחצו "הצטרפות למפגש".</p>
        </>
      );
    }
  } else if (site.video.url) {
    const v = site.video;
    body = (
      <>
        <div className="st-eyebrow">
          <Icon n="play" s={13} fill />
          היריד הסתיים · ההקלטה זמינה
        </div>
        <h2>{v.title || 'צפו בהקלטת היריד'}</h2>
        <ExtLink
          className="st-video"
          href={v.url}
          label="הקלטת היריד ב-YouTube"
          onOpen={() => track('fair_video_play')}
          aria-label={`צפייה ב${v.title || 'הקלטת היריד'} (נפתח בלשונית חדשה)`}
        >
          {v.poster && <img src={v.poster} alt="" />}
          <span className="vc-play">
            <Icon n="play" s={22} fill />
          </span>
          {v.duration && <span className="st-dur num">{v.duration}</span>}
        </ExtLink>
        <p className="st-sub">
          תודה שהשתתפתם. המידע על היחידות נשאר זמין לעיון, והגשת מועמדות להתמחות מתבצעת במערכת
          קודקס.
        </p>
      </>
    );
  } else {
    body = (
      <>
        <div className="st-eyebrow">
          <Icon n="check" s={15} />
          היריד הסתיים
        </div>
        <h2>תודה שהשתתפתם</h2>
        <p className="st-sub">
          המפגשים הסתיימו, והמידע על היחידות נשאר זמין לעיון. הגשת מועמדות להתמחות מתבצעת במערכת
          קודקס.
        </p>
      </>
    );
  }

  return (
    <section className="status" aria-labelledby="status-h">
      <span id="status-h" className="sr-only">
        מצב האירוע
      </span>
      {/* Announced on state changes only. The countdown above ticks every second and is silent. */}
      <span className="sr-only" role="status">
        {statusAnnouncement(ph)}
      </span>
      {body}
    </section>
  );
}
